import {
	PlusCircleOutlined,
	EyeOutlined,
	SyncOutlined,
	DownOutlined,
	UpOutlined,
	InfoCircleOutlined,
	VerticalAlignTopOutlined,
	RadarChartOutlined,
} from '@ant-design/icons';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import KeplerService from '../../domain/Kepler/KeplerService';
import dayjs from 'dayjs';
import { m } from '@/paraglide/messages';
import { useLocale } from '@/contexts/LocaleContext';
import {
	Modal,
	Button,
	Col,
	Form,
	Row,
	Select,
	notification,
	InputNumber,
	Collapse,
	Typography,
	Tooltip,
} from 'antd';
import {
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
} from '@hydrodigital/combobox';
import { Button as DSButton } from '@hydrodigital/design-system';

import { getModelFiles } from '../../services/model';

const keplerService = new KeplerService();
interface Props {
	showKeplerAnalysisModal: boolean;
	onCancel: () => void;
	onSuccess: (response?: any) => void;
	selectedLocation?: number;
	selectedNetworkType?: string | null;
	contextValue?: any;
}

type ModelFile = { inpName: string; inpId: string };

type PanelHeaderProps = { active: boolean; title: string; icon: React.ReactNode };
const PanelHeader = ({ active, title, icon }: PanelHeaderProps) => (
	<span
		style={{
			display: 'flex',
			alignItems: 'center',
			gap: 8,
			color: active ? '#000' : '#75798f',
		}}
	>
		<span
			style={{
				display: 'inline-flex',
				width: 18,
				height: 18,
				alignItems: 'center',
				justifyContent: 'center',
				color: active ? '#000' : '#75798f',
			}}
		>
			{icon}
		</span>
		<span style={{ fontWeight: 600, color: active ? '#000' : '#75798f' }}>{title}</span>
	</span>
);

export default function ModalKeplerAnalysisHistorial({
	showKeplerAnalysisModal,
	onCancel,
	onSuccess,
	selectedLocation,
	selectedNetworkType,
	contextValue,
}: Props) {
	useLocale();
	const [modelFiles, setModelFiles] = useState<{ label: string; value: string }[]>([]);
	const [disableButton, setDisableButton] = useState(true);
	const [messageModal, setMessageModal] = useState('');
	const [secondMessageModal, setSecondMessageModal] = useState('');
	const [modelIdSelected, setModelIdSelected] = useState(0);
	const [modelLabelSelected, setModelLabelSelected] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [hasResponse, setHasResponse] = useState(null);
	const [hasEndResponse, setHasEndResponse] = useState(null);
	const startDate = contextValue.selectedDate[0];
	const endDate = contextValue.selectedDate[1];
	const isWaterAnalysis = String(selectedNetworkType ?? '').toUpperCase() === 'WATER';
	const analysisType = isWaterAnalysis ? 'WATER' : 'SEWER';
	const modalTitle = isWaterAnalysis ? 'Water Analysis' : 'Hydraulic Analysis';
	const modalDescription = isWaterAnalysis
		? 'The Water Analysis uses the Digital Twin to generate an interactive spatial report for drinking water networks. This process will analyze the period of time between'
		: 'The hydraulic analysis uses a digital twin to perform a series of tests to help you understand and optimize your collection system. This process will analyze the period of time between';
	const modalExpandedDescription = isWaterAnalysis
		? ' The analysis helps operators and commercial teams identify potential leakage-prone areas, low-pressure zones, abnormal consumption patterns, high-consumption users, and measured versus unmeasured water volumes. The tool combines network model information, consumption readings, pressure behavior, sector-level flow patterns, and available sensor data to support operational and commercial decision-making. Next, the platform will assess Minimum Night Flow behavior to identify sectors with potential leakage events. It will compare observed flow and pressure patterns against expected hydraulic behavior to determine whether the anomaly is significant, persistent, and hydraulically consistent with leakage. Finally, the results will be presented in an interactive map-based report, allowing users to explore probable leakage zones, consumption heat maps, special clients, zero-consumption clients, reading types, connection types, and other relevant drinking water insights '
		: ' Next, the tool will run the constraint analysis, which identifies key performance issues such as surcharging pipes, backflow, high hydraulic grade line (HGL) variances, flooding, and sediment buildup. Next, the tool designs your sensor network, automatically recommending optimal sensor placement based on identified constraints. Next, the tool conducts an opportunity assessment to locate areas suitable for improvements like inline storage (ILS) and dynamic underflow control (DUC), helping you leverage existing infrastructure for better performance. Finally, all results are presented through an interactive map, giving you a clear, visual representation of constraints, sensor locations, and improvement opportunities in one intuitive view.';
	const [unit, setUnit] = useState<'METRIC' | 'US_CUSTOMARY'>('METRIC');
	const unitLabel = unit === 'US_CUSTOMARY' ? 'ft' : 'meters';
	const velocityUnitLabel = unit === 'US_CUSTOMARY' ? 'fps' : 'm/s';
	const [showMore, setShowMore] = useState(false);
	const [surchargeFactor, setSurchargeFactor] = useState<number | undefined>(1);
	const [backflowVelocityThreshold, setBackflowVelocityThreshold] = useState<number | undefined>(
		-0.01,
	);
	const [waitPeriodHours, setWaitPeriodHours] = useState<number | undefined>(1);
	const [minWaterDepthThreshold, setMinWaterDepthThreshold] = useState<number | undefined>(
		unit === 'US_CUSTOMARY' ? 0.5 : 0.15,
	);
	const [sensorGroupSpacing, setSensorGroupSpacing] = useState<number | undefined>(
		unit === 'US_CUSTOMARY' ? 1000 : 300,
	);
	const [smallDiameterThreshold, setSmallDiameterThreshold] = useState<number | undefined>(
		unit === 'US_CUSTOMARY' ? 1 : 0.3,
	);
	const [ilsSurchargeFactor, setIlsSurchargeFactor] = useState<number | undefined>(0.5);
	const [ilsMinPipeHeight, setIlsMinPipeHeight] = useState<number | undefined>(
		unit === 'US_CUSTOMARY' ? 5 : 1.5,
	);
	const [ilsMinTotalLength, setIlsMinTotalLength] = useState<number | undefined>(
		unit === 'US_CUSTOMARY' ? 1000 : 300,
	);
	const [ducMinOutfallOffset, setDucMinOutfallOffset] = useState<number | undefined>(
		unit === 'US_CUSTOMARY' ? 0.2 : 0.06,
	);
	const [ducMinDistanceToRegulator, setDucMinDistanceToRegulator] = useState<number | undefined>(
		unit === 'US_CUSTOMARY' ? 200 : 60,
	);
	const [ducMaxInterceptorSurcharge, setDucMaxInterceptorSurcharge] = useState<number | undefined>(
		0.7,
	);
	const [activeKey, setActiveKey] = useState<string | undefined>(undefined);
	const [hglSlopeHglDifferencePercent, setHglSlopeHglDifferencePercent] = useState<
		number | undefined
	>(1.5);
	const [hglWaitPeriodHours, setHglWaitPeriodHours] = useState<number | undefined>(1.0);
	const [hglMinWaterDepthThreshold, setHglMinWaterDepthThreshold] = useState<number | undefined>(
		unit === 'US_CUSTOMARY' ? 0.5 : 0.15,
	);
	const [hglMaxLinkOutletOffset, setHglMaxLinkOutletOffset] = useState<number | undefined>(
		unit === 'US_CUSTOMARY' ? 2 : 0.6,
	);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const footerRef = useRef<HTMLDivElement | null>(null);
	const [footerHeight, setFooterHeight] = useState(0);
	const opportunitiesScrollRef = useRef<HTMLDivElement | null>(null);
	const [isSmallDevice, setIsSmallDevice] = useState(false);
	const collapseContainerRef = useRef<HTMLDivElement | null>(null);
	const [collapseMaxHeight, setCollapseMaxHeight] = useState(420);
	const constraintsContentRef = useRef<HTMLDivElement | null>(null);
	const constraintsHeaderRef = useRef<HTMLSpanElement | null>(null);
	const constraintsSecondRef = useRef<HTMLDivElement | null>(null);
	const sensorHeaderRef = useRef<HTMLSpanElement | null>(null);
	const opportunitiesHeaderRef = useRef<HTMLSpanElement | null>(null);
	const [constraintsMaxHeight, setConstraintsMaxHeight] = useState<number | undefined>(undefined);
	const [headersReserve, setHeadersReserve] = useState(132);
	const resetRunState = useCallback(() => {
		if (intervalRef.current) clearInterval(intervalRef.current);
		intervalRef.current = null;
		setHasResponse(null);
		setHasEndResponse(null);
		setIsLoading(false);
	}, []);
	useEffect(() => {
		const check = () => setIsSmallDevice(window.innerWidth < 992 || window.innerHeight < 800);
		check();
		window.addEventListener('resize', check);
		return () => window.removeEventListener('resize', check);
	}, [showKeplerAnalysisModal]);
	useEffect(() => {
		let t: any;
		const compute = () => {
			if (!collapseContainerRef.current) return;
			const rect = collapseContainerRef.current.getBoundingClientRect();
			const avail = Math.max(
				260,
				Math.min(window.innerHeight - rect.top - Math.max(footerHeight || 0, 64) - 24, 520),
			);
			setCollapseMaxHeight(avail);
		};
		compute();
		t = setTimeout(compute, 60);
		window.addEventListener('resize', compute);
		return () => {
			clearTimeout(t);
			window.removeEventListener('resize', compute);
		};
	}, [footerHeight, showKeplerAnalysisModal, activeKey]);
	const computeConstraintsHeight = useCallback(() => {
		if (!constraintsContentRef.current || activeKey !== 'constraints') return;
		const base = constraintsContentRef.current.getBoundingClientRect();
		const second = constraintsSecondRef.current?.getBoundingClientRect();
		if (second) {
			const h = Math.max(160, Math.round(second.bottom - base.top) + 8);
			setConstraintsMaxHeight(h);
		}
	}, [activeKey, unit]);
	useEffect(() => {
		let r1 = 0;
		let r2 = 0;
		let t1: any;
		const run = () => {
			r1 = requestAnimationFrame(() => {
				r2 = requestAnimationFrame(() => computeConstraintsHeight());
			});
		};
		run();
		t1 = setTimeout(() => computeConstraintsHeight(), 60);
		window.addEventListener('resize', computeConstraintsHeight);
		let ro: any;
		if ((window as any).ResizeObserver && constraintsContentRef.current) {
			ro = new (window as any).ResizeObserver(() => computeConstraintsHeight());
			ro.observe(constraintsContentRef.current);
			if (constraintsSecondRef.current) ro.observe(constraintsSecondRef.current);
		}
		return () => {
			window.removeEventListener('resize', computeConstraintsHeight);
			cancelAnimationFrame(r1);
			cancelAnimationFrame(r2);
			clearTimeout(t1);
			if (ro) ro.disconnect();
		};
	}, [computeConstraintsHeight, footerHeight, showKeplerAnalysisModal]);
	useEffect(() => {
		const measure = () => {
			const ch =
				constraintsHeaderRef.current?.parentElement?.offsetHeight ||
				constraintsHeaderRef.current?.offsetHeight ||
				0;
			const h1 =
				sensorHeaderRef.current?.parentElement?.offsetHeight ||
				sensorHeaderRef.current?.offsetHeight ||
				0;
			const h2 =
				opportunitiesHeaderRef.current?.parentElement?.offsetHeight ||
				opportunitiesHeaderRef.current?.offsetHeight ||
				0;
			const reserve = Math.max(ch + h1 + h2 + 24, 160);
			setHeadersReserve(reserve);
		};
		let r = requestAnimationFrame(measure);
		window.addEventListener('resize', measure);
		let ro: any;
		if ((window as any).ResizeObserver) {
			ro = new (window as any).ResizeObserver(measure);
			if (constraintsHeaderRef.current) ro.observe(constraintsHeaderRef.current);
			if (sensorHeaderRef.current) ro.observe(sensorHeaderRef.current);
			if (opportunitiesHeaderRef.current) ro.observe(opportunitiesHeaderRef.current);
		}
		return () => {
			cancelAnimationFrame(r);
			window.removeEventListener('resize', measure);
			if (ro) ro.disconnect();
		};
	}, [activeKey, showKeplerAnalysisModal]);
	useEffect(() => {
		if (!showKeplerAnalysisModal) {
			setConstraintsMaxHeight(undefined);
			if (constraintsContentRef.current) constraintsContentRef.current.scrollTop = 0;
		}
	}, [showKeplerAnalysisModal]);
	useEffect(() => {
		if (showKeplerAnalysisModal && activeKey === 'constraints') {
			if (constraintsContentRef.current) constraintsContentRef.current.scrollTop = 0;
			requestAnimationFrame(() => computeConstraintsHeight());
		}
	}, [showKeplerAnalysisModal, activeKey, computeConstraintsHeight]);
	const ensureVisible = useCallback(
		(el: HTMLElement | null) => {
			if (!el || !opportunitiesScrollRef.current) return;
			const c = opportunitiesScrollRef.current;
			const cRect = c.getBoundingClientRect();
			const elRect = el.getBoundingClientRect();
				const extra = Math.max((footerHeight || 0) + 8, 56);
			const safeBottom = cRect.bottom - extra;
			if (elRect.bottom > safeBottom) {
				c.scrollTop += elRect.bottom - safeBottom + 6;
			}
			if (elRect.top < cRect.top + 6) {
				c.scrollTop -= cRect.top + 6 - elRect.top;
			}
		},
		[footerHeight],
	);
	useEffect(() => {
		const update = () => setFooterHeight(footerRef.current?.offsetHeight || 0);
		update();
		window.addEventListener('resize', update);
		return () => window.removeEventListener('resize', update);
	}, [showKeplerAnalysisModal, isLoading, hasEndResponse]);
	useEffect(() => {
		if (!footerRef.current || !(window as any).ResizeObserver) return;
		const ro = new (window as any).ResizeObserver((entries: any) => {
			const entry = entries[0];
			if (entry) setFooterHeight(Math.round(entry.contentRect.height));
		});
		ro.observe(footerRef.current);
		return () => ro.disconnect();
	}, [footerRef.current]);
	useEffect(() => {
		const def = unit === 'US_CUSTOMARY' ? 0.5 : 0.15;
		const isDefaultCandidate = (v: number | undefined) =>
			v == null || v === 0.5 || v === 0.15 || v === 0.1 || v === 0.03;
		if (isDefaultCandidate(minWaterDepthThreshold)) {
			setMinWaterDepthThreshold(def);
		}
		if (isDefaultCandidate(hglMinWaterDepthThreshold)) {
			setHglMinWaterDepthThreshold(def);
		}
		const defOffset = unit === 'US_CUSTOMARY' ? 2 : 0.6;
		const isDefaultOffset = (v: number | undefined) =>
			v == null || v === 2 || v === 0.6 || v === 0.0;
		if (isDefaultOffset(hglMaxLinkOutletOffset)) {
			setHglMaxLinkOutletOffset(defOffset);
		}
		const defSpacing = unit === 'US_CUSTOMARY' ? 1000 : 300;
		const isDefaultSpacing = (v: number | undefined) =>
			v == null || v === 1000 || v === 300 || v === 0.0;
		if (isDefaultSpacing(sensorGroupSpacing)) {
			setSensorGroupSpacing(defSpacing);
		}
		const defSmall = unit === 'US_CUSTOMARY' ? 1 : 0.3;
		const isDefaultSmall = (v: number | undefined) =>
			v == null || v === 1 || v === 0.3 || v === 0.0;
		if (isDefaultSmall(smallDiameterThreshold)) {
			setSmallDiameterThreshold(defSmall);
		}
		const defIlsMinHeight = unit === 'US_CUSTOMARY' ? 5 : 1.5;
		const isDefaultIlsMinHeight = (v: number | undefined) =>
			v == null || v === 5 || v === 1.5 || v === 0.0;
		if (isDefaultIlsMinHeight(ilsMinPipeHeight)) {
			setIlsMinPipeHeight(defIlsMinHeight);
		}
		const defIlsMinTotalLen = unit === 'US_CUSTOMARY' ? 1000 : 300;
		const isDefaultIlsMinTotalLen = (v: number | undefined) =>
			v == null || v === 1000 || v === 300 || v === 0.0;
		if (isDefaultIlsMinTotalLen(ilsMinTotalLength)) {
			setIlsMinTotalLength(defIlsMinTotalLen);
		}
		const defDucMinOutfall = unit === 'US_CUSTOMARY' ? 0.2 : 0.06;
		const isDefaultDucMinOutfall = (v: number | undefined) =>
			v == null || v === 0.2 || v === 0.06 || v === 0.0;
		if (isDefaultDucMinOutfall(ducMinOutfallOffset)) {
			setDucMinOutfallOffset(defDucMinOutfall);
		}
		const defDucMinDistToReg = unit === 'US_CUSTOMARY' ? 200 : 60;
		const isDefaultDucMinDistToReg = (v: number | undefined) =>
			v == null || v === 200 || v === 60 || v === 0.0;
		if (isDefaultDucMinDistToReg(ducMinDistanceToRegulator)) {
			setDucMinDistanceToRegulator(defDucMinDistToReg);
		}
	}, [unit]);

	const handleChange = async (item) => {
		const modelId = parseInt(item.value);
		setModelIdSelected(modelId);
		setModelLabelSelected(item.label);
	};

	const validateInputs = (): boolean => {
		if (isWaterAnalysis) {
			setFieldErrors({});
			return true;
		}

		const errors: Record<string, string> = {};
		const simHours = Math.max(dayjs(endDate).diff(startDate, 'hour'), 0);
		const gt0 = (v: number | undefined, key: string) => {
			if (v === undefined || !(v > 0)) errors[key] = 'Value must be greater than 0';
		};
		const ge0 = (v: number | undefined, key: string) => {
			if (v === undefined || v < 0) errors[key] = 'Value must be greater or equal to 0';
		};
		const ge0WithinSim = (v: number | undefined, key: string) => {
			if (v === undefined || v < 0) {
				errors[key] = 'Value must be greater or equal to 0';
			} else if (v > simHours) {
				errors[key] = `Value must be ≤ selected simulation time (${simHours} hours)`;
			}
		};
		gt0(surchargeFactor, 'surchargeFactor');
		ge0WithinSim(waitPeriodHours, 'waitPeriodHours');
		ge0(minWaterDepthThreshold, 'minWaterDepthThreshold');
		gt0(hglSlopeHglDifferencePercent, 'hglSlopeHglDifferencePercent');
		ge0WithinSim(hglWaitPeriodHours, 'hglWaitPeriodHours');
		ge0(hglMinWaterDepthThreshold, 'hglMinWaterDepthThreshold');
		ge0(hglMaxLinkOutletOffset, 'hglMaxLinkOutletOffset');
		gt0(sensorGroupSpacing, 'sensorGroupSpacing');
		gt0(smallDiameterThreshold, 'smallDiameterThreshold');
		gt0(ilsSurchargeFactor, 'ilsSurchargeFactor');
		ge0(ilsMinPipeHeight, 'ilsMinPipeHeight');
		ge0(ilsMinTotalLength, 'ilsMinTotalLength');
		ge0(ducMinOutfallOffset, 'ducMinOutfallOffset');
		ge0(ducMinDistanceToRegulator, 'ducMinDistanceToRegulator');
		ge0(ducMaxInterceptorSurcharge, 'ducMaxInterceptorSurcharge');
		setFieldErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const goAnalysis = async () => {
		const ok = validateInputs();
		if (!ok) {
			notification.error({
				message: 'Invalid inputs',
				description: 'Please fix the highlighted inputs before running the analysis.',
			});
			return;
		}

		const constraints = isWaterAnalysis
			? {}
			: {
					surchargeFactor: surchargeFactor,
					backflowThreshold: backflowVelocityThreshold,
					waitPeriodHours: waitPeriodHours,
					minDepthThreshold: minWaterDepthThreshold,
					hglDeltaThreshold: hglSlopeHglDifferencePercent,
					minFlowDepth: minWaterDepthThreshold,
					maxLinkOutOffset: hglMaxLinkOutletOffset,
			  };
		const sensorPlacement = isWaterAnalysis
			? {}
			: {
					minimumDistanceBetweenNodes: sensorGroupSpacing,
					smallDiameterThreshold: smallDiameterThreshold,
			  };
		const opportunities = isWaterAnalysis
			? {}
			: {
					surchargeFactor: ilsSurchargeFactor,
					minPipeHeight: ilsMinPipeHeight,
					minTotalLength: ilsMinTotalLength,
					outfallMinOffset: ducMinOutfallOffset,
					interceptorMinDistanceToReg: ducMinDistanceToRegulator,
					maxInterceptorSurcharge: ducMaxInterceptorSurcharge,
			  };

		setIsLoading(true);
		setDisableButton(true);
		openInformationStatus();
		const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD HH:mm:ss');
		const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD HH:mm:ss');
		try {
			const response = await keplerService.postGenerateKeplerHistorical(
				modelIdSelected,
				formattedStartDate,
				formattedEndDate,
				modelLabelSelected,
				analysisType,
				isWaterAnalysis ? 'METRIC' : unit,
				constraints,
				sensorPlacement,
				opportunities,
			);
			if (response) {
				setHasResponse(response);
				onSuccess({
					...(response ?? {}),
					selectedModelName: modelLabelSelected,
				});
				onCancel();
			}
		} catch (error) {
			setIsLoading(false);
			setDisableButton(false);
			notification.error({
				message: 'Request failed',
				description: 'Could not start the analysis. Please try again.',
			});
		}
	};

	const getDiffenceInMinutes = () => {
		const now = dayjs();
		const diffMinutes = now.diff(contextValue.selectedDate[0], 'minute');
		return diffMinutes;
	};

	const handleUpdateModelFiles = async () => {
		let organizationId = [];
		const printItems = localStorage.getItem('security');
		const diffMinutes = getDiffenceInMinutes();

		if (diffMinutes < 30) {
			setDisableButton(true);
			return;
		}

		if (printItems) {
			organizationId = JSON.parse(printItems).organizationId;
		}
		if (organizationId.length > 0) {
			getModelFiles(organizationId, {
				page: 1,
				size: 1000,
				locationId: selectedLocation,
			})
				.then((response) => {
					const modelOptions = getModels(response.getAllResponseDTOList);

					if (response.getAllResponseDTOList && response.getAllResponseDTOList.length > 0) {
						setDisableButton(false);
						setSecondMessageModal('');
						setMessageModal('');
					} else {
						setDisableButton(true);
						setSecondMessageModal(
							'You need an enabled model and least one data source generated by such model.',
						);
					}
					setModelFiles(modelOptions);
				})
				.catch((error) => {
					console.error(error);
				});
		}
	};

	const getModels = (modelFiles: ModelFile[]): { label: string; value: string }[] => {
		return modelFiles.map((model) => ({
			label: model.inpName,
			value: model.inpId,
		}));
	};

	useEffect(() => {
		evaluateDateTimeREstrictions();
	}, [showKeplerAnalysisModal]);

	useEffect(() => {
		if (showKeplerAnalysisModal && isWaterAnalysis) {
			setUnit('METRIC');
		}
	}, [isWaterAnalysis, showKeplerAnalysisModal]);

	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (showKeplerAnalysisModal) {
			resetRunState();
		}
	}, [resetRunState, showKeplerAnalysisModal]);

	useEffect(() => {
		const fetchHistorical = async () => {
			try {
				setIsLoading(true);
				setDisableButton(true);

				const response = await keplerService.getKeplerHistorical(selectedLocation);

				if (Array.isArray(response) && response.length === 0) {
					if (intervalRef.current) clearInterval(intervalRef.current);

					setIsLoading(false);
					setDisableButton(false);

					setModelIdSelected(0);
					setModelLabelSelected('');

					return;
				}

				if (response[0]?.status === 'COMPLETED') {
					setHasEndResponse(response[0]);
					setIsLoading(false);
					setDisableButton(false);
					if (intervalRef.current) clearInterval(intervalRef.current);
				}
			} catch (error) {
				console.error(error);
			}
		};

		intervalRef.current = setInterval(() => {
			if (hasResponse) {
				fetchHistorical();
			}
		}, 5000);

		return () => {
			if (intervalRef.current) clearInterval(intervalRef.current);
		};
	}, [hasResponse]);

	const evaluateDateTimeREstrictions = async () => {
		const diffMinutes = getDiffenceInMinutes();

		if (diffMinutes <= 30 && diffMinutes >= 0) {
			// Current time is within 30 minutes after selectedTime1
			setMessageModal(
				'You can only analize historical data. You start time must be at least 30 min. in the past.',
			);
			setSecondMessageModal('');
		} else {
			setMessageModal('');
			setSecondMessageModal('');
			if (showKeplerAnalysisModal) {
				await handleUpdateModelFiles();
			}
		}
	};

	const goOut = () => {
		if (hasEndResponse) {
			setModelIdSelected(0);
			setModelLabelSelected('');
			setDisableButton(false);
		}
		resetRunState();
		if (constraintsContentRef.current) constraintsContentRef.current.scrollTop = 0;
		setConstraintsMaxHeight(undefined);
		setActiveKey(undefined);
		onCancel();
	};

	const openInformationStatus = () => {
		notification.open({
			message: 'Request sent',
			description:
				'Your analysis is being performed, you will be able to get it in about 10 minutes',
			type: 'success',
		});
	};

	return (
		<Modal
			style={{ marginTop: 45 }}
			open={showKeplerAnalysisModal}
			onCancel={goOut}
			zIndex={1100}
			footer={
				<div ref={footerRef}>
					<Row style={{ width: '100%', justifyContent: 'flex-end' }} gutter={8}>
						<Col>
							<DSButton
								showIcon={false}
								label="Cancel"
								onClick={goOut}
								style={{
									width: '150px',
									height: '40px',
									backgroundColor: '#F7F9FA',
									color: '#434B56',
									border: '1px solid #C4CCD5',
								}}
							>
								Cancel
							</DSButton>
						</Col>
						<Col>
							{hasEndResponse ? (
								<DSButton
									style={{
										width: '180px',
										height: '40px',
									}}
									label="View"
									iconName="eye"
									onClick={() => {
										if (hasEndResponse?.link) {
											window.open(hasEndResponse.link, '_blank');
										}
									}}
								>
								</DSButton>
							) : (
								<DSButton
									style={{
										width: '180px',
										height: '40px',
									}}
									label={isLoading ? 'Analyzing...' : 'Run analysis'}
									iconName={isLoading ? 'sync' : 'plus-circle'}
									disabled={disableButton || modelIdSelected === 0}
									onClick={() => {
										setActiveKey('');
										goAnalysis();
									}}
								></DSButton>
							)}
						</Col>
					</Row>
				</div>
			}
			width={920}
			centered
			bodyStyle={{
				maxHeight: '80vh',
				overflowY: 'hidden',
				overflowX: 'hidden',
				paddingTop: 12,
				paddingBottom: Math.max((footerHeight || 0) - 48, 24),
			}}
		>
			<div
				className="flex column"
				style={{
					paddingLeft: 16,
					paddingRight: 16,
					paddingBottom: 0,
					display: 'flex',
					flexDirection: 'column',
					height: 'auto',
				}}
			>
				<Typography.Title level={4} style={{ margin: 0 }}>
					{modalTitle}
				</Typography.Title>
				<Typography.Paragraph style={{ marginTop: 8 }}>
					{isWaterAnalysis
						? `${modalDescription} ${dayjs(startDate).format('YYYY-MM-DD hh:mm a')} and ${dayjs(endDate).format('YYYY-MM-DD hh:mm a')}.`
						: m.kepler_hydraulic_analysis_description({
								startDate: dayjs(startDate).format('YYYY-MM-DD hh:mm a'),
								endDate: dayjs(endDate).format('YYYY-MM-DD hh:mm a'),
							})}
					{showMore && modalExpandedDescription}
				</Typography.Paragraph>
				<Button
					type="link"
					style={{ alignSelf: 'flex-start', padding: 0, fontSize: 12 }}
					onClick={() =>
						setShowMore((v) => {
							const next = !v;
							if (next) setActiveKey('');
							return next;
						})
					}
					icon={
						showMore ? (
							<UpOutlined style={{ fontSize: 12 }} rev={undefined} />
						) : (
							<DownOutlined style={{ fontSize: 12 }} rev={undefined} />
						)
					}
				>
					{showMore ? 'Show less' : 'Show more'}
				</Button>
				{messageModal && (
					<div>
						<div className="flex column gap-2 mt-3">
							<span style={{ fontWeight: 500, fontSize: 14 }}>{messageModal}</span>
						</div>
					</div>
				)}
				{secondMessageModal && (
					<div>
						<div className="flex column gap-2 mt-3">
							<span style={{ fontWeight: 500, fontSize: 14 }}>{secondMessageModal}</span>
						</div>
					</div>
				)}
				{!messageModal && !secondMessageModal && (
					<>
						<div
							style={{
								overflowY: 'hidden',
								overflowX: 'hidden',
								paddingLeft: 8,
								paddingRight: 8,
								marginTop: 8,
							}}
						>
							<Row gutter={16}>
								<Col span={isWaterAnalysis ? 24 : 12}>
									<div>
										<label>
											<span style={{ color: 'red' }}> * </span>Digital Twin
										</label>
										<div style={{ marginTop: 8 }}>
											<Combobox
												items={modelFiles}
												value={modelIdSelected > 0 ? String(modelIdSelected) : undefined}
												onValueChange={(val) => {
													const v = val || '';
													if (!v) {
														setModelIdSelected(0);
														setModelLabelSelected('');
														return;
													}
													const opt = modelFiles.find((o) => String(o.value) === String(v));
													handleChange({ value: String(v), label: opt?.label || '' });
												}}
												onClear={() => {
													setModelIdSelected(0);
													setModelLabelSelected('');
												}}
												size="small"
											>
												<ComboboxInput placeholder="Select model" />
												<ComboboxContent>
													<ComboboxEmpty>No models found.</ComboboxEmpty>
													<ComboboxList>
														{(item) => (
															<ComboboxItem key={item.value} value={item}>
																{item.label}
															</ComboboxItem>
														)}
													</ComboboxList>
												</ComboboxContent>
											</Combobox>
										</div>
									</div>
								</Col>
								{!isWaterAnalysis && (
									<Col span={12}>
										<div>
											<label>
												<span style={{ color: 'red' }}> * </span>Unit
											</label>
											<div style={{ marginTop: 8 }}>
												<Combobox
													items={[
														{ label: 'Metric', value: 'METRIC' },
														{ label: 'US Customary', value: 'US_CUSTOMARY' },
													]}
													value={unit}
													onValueChange={(val) => {
														const v = val || 'METRIC';
														setUnit(v as 'METRIC' | 'US_CUSTOMARY');
													}}
													onClear={() => setUnit('METRIC')}
													size="small"
												>
													<ComboboxInput placeholder="Metric" />
													<ComboboxContent>
														<ComboboxEmpty>No units found.</ComboboxEmpty>
														<ComboboxList>
															{(item) => (
																<ComboboxItem key={item.value} value={item}>
																	{item.label}
																</ComboboxItem>
															)}
														</ComboboxList>
													</ComboboxContent>
												</Combobox>
											</div>
										</div>
									</Col>
								)}
							</Row>

							{!isWaterAnalysis && (
								<div ref={collapseContainerRef} style={{ marginTop: 16, overflowX: 'hidden' }}>
									<Collapse
										accordion
										activeKey={activeKey}
										onChange={(key) => {
											const k = Array.isArray(key)
												? (key as string[])[(key as string[]).length - 1]
												: (key as string);
											setActiveKey(k);
											if (k) setShowMore(false);
											if (k === 'constraints') {
												setTimeout(() => {
													if (constraintsContentRef.current)
														constraintsContentRef.current.scrollTop = 0;
													computeConstraintsHeight();
												}, 0);
											}
										}}
										expandIconPosition="right"
									>
									<Collapse.Panel
										header={
											<span
												ref={constraintsHeaderRef}
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: 8,
													color: activeKey === 'constraints' ? '#000' : '#75798f',
												}}
											>
												<span
													style={{
														display: 'inline-flex',
														width: 18,
														height: 18,
														alignItems: 'center',
														justifyContent: 'center',
													}}
												>
													<svg
														width="18"
														height="18"
														viewBox="0 0 24 24"
														fill="currentColor"
														aria-hidden="true"
													>
														<rect x="6" y="4" width="12" height="2" />
														<path d="M12 6 L6 12 H10 V20 H14 V12 H18 L12 6 Z" />
													</svg>
												</span>
												<span style={{ fontWeight: 600 }}>Constraints</span>
											</span>
										}
										key="constraints"
									>
										<div
											ref={constraintsContentRef}
											style={{
												paddingRight: 8,
												paddingLeft: 8,
												overflowX: 'hidden',
												overflowY: 'auto',
												maxHeight: Math.min(constraintsMaxHeight ?? 9999, Math.max(collapseMaxHeight - headersReserve, 160)),
												paddingBottom: 16,
												scrollPaddingBottom: 24,
											}}
										>
											<div style={{ marginBottom: 12, fontWeight: 600 }}>
												SURCHARGING PARAMETERS (1)
											</div>
											<Row gutter={16}>
												<Col span={24}>
													Surcharge factor:
													<div style={{ position: 'relative', marginTop: 8 }}>
														<InputNumber
															style={{
																width: '100%',
																borderRadius: 24,
																height: 40,
																paddingRight: 32,
															}}
															min={0}
															precision={1}
															size="large"
															value={surchargeFactor}
															onChange={(v) =>
																setSurchargeFactor(typeof v === 'number' ? v : undefined)
															}
															placeholder="Enter value"
														/>
														<Tooltip title="Sets the threshold for when a pipe is considered surcharged, based on water depth relative to pipe height. For example, 1.0 sets the flag when the pipe is completely full; values above 1.0 allow pressurized flow before flagging.">
															<InfoCircleOutlined
																style={{
																	position: 'absolute',
																	right: 25,
																	top: '50%',
																	transform: 'translateY(-50%)',
																	color: '#75798f',
																}}
																rev={undefined}
															/>
														</Tooltip>
													</div>
													<div
														style={{
															fontSize: 12,
															marginTop: 4,
															color: fieldErrors.surchargeFactor ? '#ff4d4f' : '#75798f',
														}}
													>
														{fieldErrors.surchargeFactor || 'Float > 0'}
													</div>
												</Col>
											</Row>

											<div style={{ marginTop: 16, marginBottom: 12, fontWeight: 600 }}>
												BACKFLOW PARAMETERS (3)
											</div>
											<Row gutter={16}>
												<Col span={24}>
													Backflow Velocity Threshold ({velocityUnitLabel}):
													<div style={{ position: 'relative', marginTop: 8 }}>
														<InputNumber
															style={{
																width: '100%',
																borderRadius: 24,
																height: 40,
																paddingRight: 32,
															}}
															precision={2}
															size="large"
															value={backflowVelocityThreshold}
															onChange={(v) =>
																setBackflowVelocityThreshold(typeof v === 'number' ? v : undefined)
															}
															placeholder="Enter value"
														/>
														<Tooltip title="Sets the velocity limit for detecting reverse (backward) flow in pipes. Use 0 or a negative value to flag any backward movement of water.">
															<InfoCircleOutlined
																style={{
																	position: 'absolute',
																	right: 25,
																	top: '50%',
																	transform: 'translateY(-50%)',
																	color: '#75798f',
																}}
																rev={undefined}
															/>
														</Tooltip>
													</div>
													<div style={{ fontSize: 12, marginTop: 4, color: '#75798f' }}>
														Any float
													</div>
												</Col>
												<Col span={24} style={{ marginTop: 12 }}>
													Wait Period (hours):
													<div style={{ position: 'relative', marginTop: 8 }}>
														<InputNumber
															style={{
																width: '100%',
																borderRadius: 24,
																height: 40,
																paddingRight: 32,
															}}
															min={0}
															precision={0}
															size="large"
															value={waitPeriodHours}
															onChange={(v) =>
																setWaitPeriodHours(typeof v === 'number' ? v : undefined)
															}
															placeholder="Enter hours"
															formatter={(value) =>
																value === undefined || value === null ? '' : `${value} hours`
															}
															parser={(value) => (value ? value.replace(' hours', '') : '')}
														/>
														<Tooltip title="Sets the delay before checking for backflow, allowing the system to settle after startup. Enter hours (e.g., 1 = wait one hour).">
															<InfoCircleOutlined
																style={{
																	position: 'absolute',
																	right: 25,
																	top: '50%',
																	transform: 'translateY(-50%)',
																	color: '#75798f',
																}}
																rev={undefined}
															/>
														</Tooltip>
													</div>
													<div
														style={{
															fontSize: 12,
															marginTop: 4,
															color: fieldErrors.waitPeriodHours ? '#ff4d4f' : '#75798f',
														}}
													>
														{fieldErrors.waitPeriodHours ||
															'Float ≥ 0, Max value is selected simulation time'}
													</div>
												</Col>
												<Col span={24} style={{ marginTop: 12 }}>
													<div ref={constraintsSecondRef}>
														Minimum Water Depth Threshold ({unitLabel}):
														<div style={{ position: 'relative', marginTop: 8 }}>
															<InputNumber
																style={{
																	width: '100%',
																	borderRadius: 24,
																	height: 40,
																	paddingRight: 32,
																}}
																min={0}
																precision={1}
																size="large"
																value={minWaterDepthThreshold}
																onChange={(v) =>
																	setMinWaterDepthThreshold(typeof v === 'number' ? v : undefined)
																}
																placeholder={`Enter ${unitLabel}`}
																formatter={(value) =>
																	value === undefined || value === null
																		? ''
																		: `${value} ${unitLabel}`
																}
																parser={(value) =>
																	value ? value.replace(` ${unitLabel}`, '') : ''
																}
															/>
															<Tooltip
																title={`Sets the minimum water depth required to consider backflow conditions. Values below this threshold are ignored. Enter ${unitLabel}.`}
															>
																<InfoCircleOutlined
																	style={{
																		position: 'absolute',
																		right: 25,
																		top: '50%',
																		transform: 'translateY(-50%)',
																		color: '#75798f',
																	}}
																	rev={undefined}
																/>
															</Tooltip>
														</div>
														<div
															style={{
																fontSize: 12,
																marginTop: 4,
																color: fieldErrors.minWaterDepthThreshold ? '#ff4d4f' : '#75798f',
															}}
														>
															{fieldErrors.minWaterDepthThreshold || 'Float ≥ 0'}
														</div>
													</div>
												</Col>
											</Row>
											<div style={{ marginTop: 16, marginBottom: 12, fontWeight: 600 }}>
												HIGH HYDRAULIC GRADE LINE (HGL) VARIANCE (4)
											</div>
											<Row gutter={16} style={{ paddingBottom: isSmallDevice ? '50px' : '15px' }}>
												<Col span={24}>
													Difference Between Pipe Slope and Hydraulic Grade Line:
													<div style={{ position: 'relative', marginTop: 8 }}>
														<InputNumber
															style={{
																width: '100%',
																borderRadius: 24,
																height: 40,
																paddingRight: 32,
															}}
															min={0}
															precision={1}
															size="large"
															value={hglSlopeHglDifferencePercent}
															onChange={(v) =>
																setHglSlopeHglDifferencePercent(
																	typeof v === 'number' ? v : undefined,
																)
															}
															placeholder="Enter value"
														/>
														<Tooltip title="Sets how much the hydraulic grade line (pressure) slope may differ from the pipe slope before flagging. For example, 1.5 allows up to a 150% difference. ">
															<InfoCircleOutlined
																style={{
																	position: 'absolute',
																	right: 25,
																	top: '50%',
																	transform: 'translateY(-50%)',
																	color: '#75798f',
																}}
																rev={undefined}
															/>
														</Tooltip>
													</div>
													<div
														style={{
															fontSize: 12,
															marginTop: 4,
															color: fieldErrors.hglSlopeHglDifferencePercent
																? '#ff4d4f'
																: '#75798f',
														}}
													>
														{fieldErrors.hglSlopeHglDifferencePercent || 'Float > 0'}
													</div>
												</Col>
												<Col span={24} style={{ marginTop: 12 }}>
													Wait Period (hours):
													<div style={{ position: 'relative', marginTop: 8 }}>
														<InputNumber
															style={{
																width: '100%',
																borderRadius: 24,
																height: 40,
																paddingRight: 32,
															}}
															min={0}
															precision={0}
															size="large"
															value={hglWaitPeriodHours}
															onChange={(v) =>
																setHglWaitPeriodHours(typeof v === 'number' ? v : undefined)
															}
															placeholder="Enter hours"
															formatter={(value) =>
																value === undefined || value === null ? '' : `${value} hours`
															}
															parser={(value) => (value ? value.replace(' hours', '') : '')}
														/>
														<Tooltip title="Sets the delay before checking for high HGL variance, allowing the system to settle after startup. Enter hours (e.g., 1 = wait one hour). ">
															<InfoCircleOutlined
																style={{
																	position: 'absolute',
																	right: 25,
																	top: '50%',
																	transform: 'translateY(-50%)',
																	color: '#75798f',
																}}
																rev={undefined}
															/>
														</Tooltip>
													</div>
													<div
														style={{
															fontSize: 12,
															marginTop: 4,
															color: fieldErrors.hglWaitPeriodHours ? '#ff4d4f' : '#75798f',
														}}
													>
														{fieldErrors.hglWaitPeriodHours ||
															'Float ≥ 0, Max value is selected simulation time'}
													</div>
												</Col>
												<Col span={24} style={{ marginTop: 12 }}>
													Minimum Water Depth Threshold ({unitLabel}):
													<div style={{ position: 'relative', marginTop: 8 }}>
														<InputNumber
															style={{
																width: '100%',
																borderRadius: 24,
																height: 40,
																paddingRight: 32,
															}}
															min={0}
															precision={1}
															size="large"
															value={hglMinWaterDepthThreshold}
															onChange={(v) =>
																setHglMinWaterDepthThreshold(typeof v === 'number' ? v : undefined)
															}
															placeholder={`Enter ${unitLabel}`}
															formatter={(value) =>
																value === undefined || value === null ? '' : `${value} ${unitLabel}`
															}
															parser={(value) => (value ? value.replace(` ${unitLabel}`, '') : '')}
														/>
														<Tooltip title="Sets the minimum water depth required before HGL variance is evaluated, ignoring shallow flow that may not reflect true pressure behavior. ">
															<InfoCircleOutlined
																style={{
																	position: 'absolute',
																	right: 25,
																	top: '50%',
																	transform: 'translateY(-50%)',
																	color: '#75798f',
																}}
																rev={undefined}
															/>
														</Tooltip>
													</div>
													<div
														style={{
															fontSize: 12,
															marginTop: 4,
															color: fieldErrors.hglMinWaterDepthThreshold ? '#ff4d4f' : '#75798f',
														}}
													>
														{fieldErrors.hglMinWaterDepthThreshold || 'Float ≥ 0'}
													</div>
												</Col>
												<Col span={24} style={{ marginTop: 12, marginBottom: 16 }}>
													Maximum Link Outlet Offset ({unitLabel}):
													<div style={{ position: 'relative', marginTop: 8 }}>
														<InputNumber
															style={{
																width: '100%',
																borderRadius: 24,
																height: 40,
																paddingRight: 32,
															}}
															min={0}
															precision={1}
															size="large"
															value={hglMaxLinkOutletOffset}
															onChange={(v) =>
																setHglMaxLinkOutletOffset(typeof v === 'number' ? v : undefined)
															}
															placeholder={`Enter ${unitLabel}`}
															formatter={(value) =>
																value === undefined || value === null ? '' : `${value} ${unitLabel}`
															}
															parser={(value) => (value ? value.replace(` ${unitLabel}`, '') : '')}
														/>
														<Tooltip title="Sets the maximum allowed outlet elevation offset to prevent false flags on pipes with significant outlet drops (cascading flow). ">
															<InfoCircleOutlined
																style={{
																	position: 'absolute',
																	right: 25,
																	top: '50%',
																	transform: 'translateY(-50%)',
																	color: '#75798f',
																}}
																rev={undefined}
															/>
														</Tooltip>
													</div>
													<div
														style={{
															fontSize: 12,
															marginTop: 4,
															color: fieldErrors.hglMaxLinkOutletOffset ? '#ff4d4f' : '#75798f',
														}}
													>
														{fieldErrors.hglMaxLinkOutletOffset || 'Float ≥ 0'}
													</div>
												</Col>
											</Row>
										</div>
									</Collapse.Panel>
									<Collapse.Panel
										header={
											<span ref={sensorHeaderRef}>
												<PanelHeader
													active={activeKey === 'sensor'}
													title="Sensor Placement"
													icon={
														<svg
															width="18"
															height="18"
															viewBox="0 0 24 24"
															fill="currentColor"
															aria-hidden="true"
														>
															<circle cx="12" cy="12" r="2" />
															<path
																d="M4.929 4.929a10 10 0 0 0 0 14.142M19.071 4.929a10 10 0 0 1 0 14.142"
																stroke="currentColor"
																strokeWidth="2"
																fill="none"
																strokeLinecap="round"
															/>
															<path
																d="M8.464 8.464a6 6 0 0 0 0 8.486M15.536 8.464a6 6 0 0 1 0 8.486"
																stroke="currentColor"
																strokeWidth="2"
																fill="none"
																strokeLinecap="round"
															/>
														</svg>
													}
												/>
											</span>
										}
										key="sensor"
									>
										<div
											style={{
												overflowY: 'auto',
												maxHeight: Math.max(collapseMaxHeight - headersReserve, 160),
												paddingRight: 8,
												paddingBottom: 16,
												scrollPaddingBottom: 24,
											}}
										>
											<Row gutter={16}>
												<Col span={24}>
													Sensor Group Spacing ({unitLabel}):
													<div style={{ position: 'relative', marginTop: 8 }}>
														<InputNumber
															style={{
																width: '100%',
																borderRadius: 24,
																height: 40,
																paddingRight: 32,
															}}
															min={0}
															precision={1}
															size="large"
															value={sensorGroupSpacing}
															onChange={(v) =>
																setSensorGroupSpacing(typeof v === 'number' ? v : undefined)
															}
															placeholder={`Enter ${unitLabel}`}
															formatter={(value) =>
																value === undefined || value === null ? '' : `${value} ${unitLabel}`
															}
															parser={(value) => (value ? value.replace(` ${unitLabel}`, '') : '')}
														/>
														<Tooltip title="Sets the minimum spacing between sensors within the same group of nearby constrained pipes (e.g., pipes flagged for surcharge, backflow, high HGL variance). This spreads sensors out inside each group based on the constraints detected. Note: If separate groups are close to each other, sensors may still be placed closer than this spacing because it only applies within groups, not between groups.">
															<InfoCircleOutlined
																style={{
																	position: 'absolute',
																	right: 25,
																	top: '50%',
																	transform: 'translateY(-50%)',
																	color: '#75798f',
																}}
																rev={undefined}
															/>
														</Tooltip>
													</div>
													<div
														style={{
															fontSize: 12,
															marginTop: 4,
															color: fieldErrors.sensorGroupSpacing ? '#ff4d4f' : '#75798f',
														}}
													>
														{fieldErrors.sensorGroupSpacing || 'Float > 0'}
													</div>
												</Col>
												<Col span={24} style={{ marginTop: 12 }}>
													Small Diameter Threshold ({unitLabel}):
													<div style={{ position: 'relative', marginTop: 8 }}>
														<InputNumber
															style={{
																width: '100%',
																borderRadius: 24,
																height: 40,
																paddingRight: 32,
															}}
															min={0}
															precision={1}
															size="large"
															value={smallDiameterThreshold}
															onChange={(v) =>
																setSmallDiameterThreshold(typeof v === 'number' ? v : undefined)
															}
															placeholder={`Enter ${unitLabel}`}
															formatter={(value) =>
																value === undefined || value === null ? '' : `${value} ${unitLabel}`
															}
															parser={(value) => (value ? value.replace(` ${unitLabel}`, '') : '')}
														/>
														<Tooltip title="Sets the pipe diameter limit for identifying small pipes, which affects sensor type selection.">
															<InfoCircleOutlined
																style={{
																	position: 'absolute',
																	right: 25,
																	top: '50%',
																	transform: 'translateY(-50%)',
																	color: '#75798f',
																}}
																rev={undefined}
															/>
														</Tooltip>
													</div>
													<div
														style={{
															fontSize: 12,
															marginTop: 4,
															color: fieldErrors.smallDiameterThreshold ? '#ff4d4f' : '#75798f',
														}}
													>
														{fieldErrors.smallDiameterThreshold || 'Float > 0'}
													</div>
												</Col>
											</Row>
										</div>
									</Collapse.Panel>
									<Collapse.Panel
										header={
											<span ref={opportunitiesHeaderRef} style={{ display: 'flex', alignItems: 'center', gap: 8, color: activeKey === 'opportunities' ? '#000' : '#75798f' }}>
												<span style={{ display: 'inline-flex', width: 18, height: 18, alignItems: 'center', justifyContent: 'center', color: activeKey === 'opportunities' ? '#000' : '#75798f' }}>
													<svg
														width="18"
														height="18"
														viewBox="0 0 24 24"
														fill="currentColor"
														aria-hidden="true"
													>
														<path d="M12 2c-4.418 0-8 3.582-8 8 0 3.1 1.7 5.7 3.8 7.4.4.3.6.7.6 1.1V19h7.2v-.5c0-.5.2-.9.6-1.1C18.3 15.7 20 13.1 20 10c0-4.418-3.582-8-8-8z" />
														<rect x="9" y="19" width="6" height="2" />
														<rect x="10" y="21" width="4" height="2" />
														<path d="M10 6c1.8-1.7 3.6-2 5.5-1.9-.5 1.1-1.2 2-2.1 2.7-1.1.9-2.4 1.4-3.6 1.6.3-.9.8-1.7 1.2-2.4" fill="#ffffff" />
													</svg>
												</span>
												<span style={{ fontWeight: 600, color: activeKey === 'opportunities' ? '#000' : '#75798f' }}>
													Opportunities
												</span>
											</span>
										}
										key="opportunities"
									>
										{(() => {
											const effectiveFooterSpace = Math.max((footerHeight || 0) + 8, 56);
											return (
												<div
													ref={opportunitiesScrollRef}
													style={{
														overflowY: 'auto',
														maxHeight: Math.max(collapseMaxHeight - 16, 220),
														paddingRight: 8,
														paddingBottom: effectiveFooterSpace,
														scrollPaddingBottom: effectiveFooterSpace,
													}}
												>
													<div style={{ marginBottom: 12, fontWeight: 600 }}>
														INLINE STORAGE (ILS) (3)
													</div>
													<Row gutter={16}>
														<Col span={24}>
															Surcharge factor:
															<div style={{ position: 'relative', marginTop: 8 }}>
																<InputNumber
																	style={{
																		width: '100%',
																		borderRadius: 24,
																		height: 40,
																		paddingRight: 32,
																	}}
																	min={0}
																	precision={2}
																	size="large"
																	value={ilsSurchargeFactor}
																	onChange={(v) =>
																		setIlsSurchargeFactor(typeof v === 'number' ? v : undefined)
																	}
																	placeholder="Enter value"
																/>
																<Tooltip title="Sets the maximum surcharge level allowed for pipes to be considered for inline storage. For example, 0.15 means pipes must stay below 15% full to qualify.">
																	<InfoCircleOutlined
																		style={{
																			position: 'absolute',
																			right: 25,
																			top: '50%',
																			transform: 'translateY(-50%)',
																			color: '#75798f',
																		}}
																		rev={undefined}
																	/>
																</Tooltip>
															</div>
															<div
																style={{
																	fontSize: 12,
																	marginTop: 4,
																	color: fieldErrors.ilsSurchargeFactor ? '#ff4d4f' : '#75798f',
																}}
															>
																{fieldErrors.ilsSurchargeFactor ||
																	'Float > 0 (recommended: 0.05–0.5)'}
															</div>
														</Col>
														<Col span={24} style={{ marginTop: 12 }}>
															Minimum Pipe Height ({unitLabel}):
															<div style={{ position: 'relative', marginTop: 8 }}>
																<InputNumber
																	style={{
																		width: '100%',
																		borderRadius: 24,
																		height: 40,
																		paddingRight: 32,
																	}}
																	min={0}
																	precision={1}
																	size="large"
																	value={ilsMinPipeHeight}
																	onChange={(v) =>
																		setIlsMinPipeHeight(typeof v === 'number' ? v : undefined)
																	}
																	placeholder={`Enter ${unitLabel}`}
																	formatter={(value) =>
																		value === undefined || value === null
																			? ''
																			: `${value} ${unitLabel}`
																	}
																	parser={(value) =>
																		value ? value.replace(` ${unitLabel}`, '') : ''
																	}
																/>
																<Tooltip title="Sets the minimum pipe diameter or height required for inline storage consideration. Pipes smaller than this value are excluded.">
																	<InfoCircleOutlined
																		style={{
																			position: 'absolute',
																			right: 25,
																			top: '50%',
																			transform: 'translateY(-50%)',
																			color: '#75798f',
																		}}
																		rev={undefined}
																	/>
																</Tooltip>
															</div>
															<div
																style={{
																	fontSize: 12,
																	marginTop: 4,
																	color: fieldErrors.ilsMinPipeHeight ? '#ff4d4f' : '#75798f',
																}}
															>
																{fieldErrors.ilsMinPipeHeight || 'Float ≥ 0'}
															</div>
														</Col>
														<Col span={24} style={{ marginTop: 12 }}>
															Minimum Total Length ({unitLabel}):
															<div style={{ position: 'relative', marginTop: 8 }}>
																<InputNumber
																	style={{
																		width: '100%',
																		borderRadius: 24,
																		height: 40,
																		paddingRight: 32,
																	}}
																	precision={1}
																	size="large"
																	value={ilsMinTotalLength}
																	onChange={(v) =>
																		setIlsMinTotalLength(typeof v === 'number' ? v : undefined)
																	}
																	placeholder={`Enter ${unitLabel}`}
																	formatter={(value) =>
																		value === undefined || value === null
																			? ''
																			: `${value} ${unitLabel}`
																	}
																	parser={(value) =>
																		value ? value.replace(` ${unitLabel}`, '') : ''
																	}
																/>
																<Tooltip title="Sets the minimum total length of interconnected pipes required for an inline storage opportunity. Systems shorter than this value are excluded.">
																	<InfoCircleOutlined
																		style={{
																			position: 'absolute',
																			right: 25,
																			top: '50%',
																			transform: 'translateY(-50%)',
																			color: '#75798f',
																		}}
																		rev={undefined}
																	/>
																</Tooltip>
															</div>
															<div
																style={{
																	fontSize: 12,
																	marginTop: 4,
																	color: fieldErrors.ilsMinTotalLength ? '#ff4d4f' : '#75798f',
																}}
															>
																{fieldErrors.ilsMinTotalLength || 'Float ≥ 0'}
															</div>
														</Col>
													</Row>
													<div style={{ marginTop: 16, marginBottom: 12, fontWeight: 600 }}>
														DYNAMIC UNDERFLOW CONTROL (DUC) (3)
													</div>
													<Row
														gutter={16}
														style={{ paddingBottom: isSmallDevice ? '50px' : '15px' }}
													>
														<Col span={24}>
															Minimum Outfall Offset ({unitLabel}):
															<div style={{ position: 'relative', marginTop: 8 }}>
																<InputNumber
																	style={{
																		width: '100%',
																		borderRadius: 24,
																		height: 40,
																		paddingRight: 32,
																	}}
																	min={0}
																	precision={1}
																	size="large"
																	value={ducMinOutfallOffset}
																	onChange={(v) =>
																		setDucMinOutfallOffset(typeof v === 'number' ? v : undefined)
																	}
																	placeholder={`Enter ${unitLabel}`}
																	formatter={(value) =>
																		value === undefined || value === null
																			? ''
																			: `${value} ${unitLabel}`
																	}
																	parser={(value) =>
																		value ? value.replace(` ${unitLabel}`, '') : ''
																	}
																/>
																<Tooltip title="Sets the minimum elevation offset required at the outfall for underflow control to be considered. Smaller offsets are excluded.">
																	<InfoCircleOutlined
																		style={{
																			position: 'absolute',
																			right: 25,
																			top: '50%',
																			transform: 'translateY(-50%)',
																			color: '#75798f',
																		}}
																		rev={undefined}
																	/>
																</Tooltip>
															</div>
															<div
																style={{
																	fontSize: 12,
																	marginTop: 4,
																	color: fieldErrors.ducMinOutfallOffset ? '#ff4d4f' : '#75798f',
																}}
															>
																{fieldErrors.ducMinOutfallOffset || 'Float ≥ 0'}
															</div>
														</Col>
														<Col span={24} style={{ marginTop: 12 }}>
															Minimum Distance to Regulator ({unitLabel}):
															<div style={{ position: 'relative', marginTop: 8 }}>
																<InputNumber
																	style={{
																		width: '100%',
																		borderRadius: 24,
																		height: 40,
																		paddingRight: 32,
																	}}
																	min={0}
																	precision={1}
																	size="large"
																	value={ducMinDistanceToRegulator}
																	onChange={(v) =>
																		setDucMinDistanceToRegulator(
																			typeof v === 'number' ? v : undefined,
																		)
																	}
																	placeholder={`Enter ${unitLabel}`}
																	formatter={(value) =>
																		value === undefined || value === null
																			? ''
																			: `${value} ${unitLabel}`
																	}
																	parser={(value) =>
																		value ? value.replace(` ${unitLabel}`, '') : ''
																	}
																/>
																<Tooltip title="Sets the minimum distance between the regulator and interceptor for underflow control opportunities. Shorter distances are excluded.">
																	<InfoCircleOutlined
																		style={{
																			position: 'absolute',
																			right: 25,
																			top: '50%',
																			transform: 'translateY(-50%)',
																			color: '#75798f',
																		}}
																		rev={undefined}
																	/>
																</Tooltip>
															</div>
															<div
																style={{
																	fontSize: 12,
																	marginTop: 4,
																	color: fieldErrors.ducMinDistanceToRegulator
																		? '#ff4d4f'
																		: '#75798f',
																}}
															>
																{fieldErrors.ducMinDistanceToRegulator || 'Float ≥ 0'}
															</div>
														</Col>
														<Col
															span={24}
															style={{
																marginTop: 12,
																marginBottom: effectiveFooterSpace,
																scrollMarginBottom: effectiveFooterSpace,
															}}
														>
															Maximum Interceptor Surcharge:
															<div style={{ position: 'relative', marginTop: 8 }}>
																<InputNumber
																	style={{
																		width: '100%',
																		borderRadius: 24,
																		height: 40,
																		paddingRight: 32,
																	}}
																	min={0}
																	precision={1}
																	size="large"
																	value={ducMaxInterceptorSurcharge}
																	onFocus={(e) => ensureVisible(e.target as HTMLElement)}
																	onChange={(v) =>
																		setDucMaxInterceptorSurcharge(
																			typeof v === 'number' ? v : undefined,
																		)
																	}
																	placeholder="Enter value"
																/>
																<Tooltip title="Sets the maximum allowable surcharge level in the interceptor for underflow control. For example, 0.7 means the interceptor can be up to 70% full before being excluded. The recommended value is between 0.5 and 0.9.">
																	<InfoCircleOutlined
																		style={{
																			position: 'absolute',
																			right: 25,
																			top: '50%',
																			transform: 'translateY(-50%)',
																			color: '#75798f',
																		}}
																		rev={undefined}
																	/>
																</Tooltip>
															</div>
															<div
																style={{
																	fontSize: 12,
																	marginTop: 4,
																	color: fieldErrors.ducMaxInterceptorSurcharge
																		? '#ff4d4f'
																		: '#75798f',
																}}
															>
																{fieldErrors.ducMaxInterceptorSurcharge || 'Float ≥ 0'}
															</div>
														</Col>
													</Row>
													<div style={{ height: 4 }} />
												</div>
											);
										})()}
									</Collapse.Panel>
									</Collapse>
								</div>
							)}
						</div>
					</>
				)}
			</div>
		</Modal>
	);
}
